/**
 * Rooms service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument, mirroring the existing app service convention. Return-shape
 * note: `get`/`patch` return `response.data`; `post` returns `{ data, status }`.
 */
import { buildQuery } from "./_query";
import { wrapCommsError } from "./_error";

/**
 * List rooms in the caller's inbox.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [params] - Optional filters: `{ room_type, client_id, sla_active, search,
 *   limit, offset }`. Paginated server-side (default 50, max 200).
 * @returns {Promise<{ rooms: Array<{ id, company_id, client_id, room_type, status,
 *   centrifugo_channel, display_id, role, unread_count, last_message, last_seq,
 *   last_activity_at?, sla_active?, sla_deadline?, created_at }> }>}
 *
 * `last_activity_at` is the timestamp of the room's last message and is what
 * the inbox ordering is based on. `last_seq` is a PER-ROOM counter and is not
 * comparable across rooms, so it must not be used for recency.
 */
/**
 * Page size for the coach chat lists. The server default is 50; a coach with
 * more clients than that silently lost the rest — including every new client,
 * who had no message to rank by yet. 200 is the server's cap.
 */
export const ROOMS_PAGE_LIMIT = 200;

export const listRooms = async (get, params) => {
  try {
    return await get(`/api/v1/rooms${buildQuery(params)}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching rooms");
  }
};

/**
 * Fetch a single room.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id.
 * @returns {Promise<{ id, company_id, client_id, room_type, status,
 *   centrifugo_channel, ai_mode, ticket_id?, display_id, archived_at?, created_at }>}
 */
export const getRoom = async (get, roomId) => {
  try {
    return await get(`/api/v1/rooms/${roomId}`);
  } catch (err) {
    const message = err?.response?.data?.message || "Error fetching room";
    console.error(message);
    throw err;
  }
};

/**
 * Get a Centrifugo subscription token for a room.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @returns {Promise<{ data: { token, channel, can_publish }, status: number }>}
 */
export const getRoomToken = async (post, roomId) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/token`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching room token");
  }
};

/**
 * List participants of a room.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id.
 * @param {boolean} [includeRemoved=true] - Include removed participants.
 * @returns {Promise<{ participants: Array<{ id, user_id, participant_role,
 *   can_write, removed_at, joined_at, coach_color_index }> }>}
 */
export const getParticipants = async (get, roomId, includeRemoved = true) => {
  try {
    return await get(`/api/v1/rooms/${roomId}/participants${buildQuery({ include_removed: includeRemoved })}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching participants");
  }
};

/**
 * Update the AI settings for a room.
 *
 * @param {Function} patch - ms-agents PATCH verb.
 * @param {string} roomId - Room id.
 * @param {"ENABLED"|"DISABLED"|"URGENCY_ONLY"} mode - AI mode.
 * @returns {Promise<Object>} Updated room settings.
 */
export const patchAiSettings = async (patch, roomId, mode) => {
  try {
    return await patch(`/api/v1/rooms/${roomId}/ai-settings`, { mode });
  } catch (err) {
    throw wrapCommsError(err, "Error updating AI settings");
  }
};

/**
 * Reassign a room to another coach/agent.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {Object} payload - Reassignment payload.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const reassignRoom = async (post, roomId, payload) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/reassign`, payload);
  } catch (err) {
    throw wrapCommsError(err, "Error reassigning room");
  }
};
